import { createTheme, PaletteMode, Theme } from "@mui/material/styles";
import { MONOCHROME_PLATFORM_ICONS } from "../utils/iconUtils";

// Fixed brand ink for "solid pill" branding accents (e.g. the company-name
// pill, the sidebar's active nav item) that are paired with the gold
// secondary50 text. Unlike theme.palette.primary (which flips with the mode
// so titles/icons stay legible), these pills are meant to always read as a
// solid dark chip in both light and dark mode — gold text contrasts against
// it either way, whereas it would not contrast against primary.main once
// that flips to a light color in dark mode.
export const BRAND_PILL_INK = "#161616";

declare module "@mui/material/styles" {
  interface Palette {
    secondary50: Palette["primary"];
    success50: Palette["primary"];
    info50: Palette["primary"];
    error50: Palette["primary"];
    neutral: {
      main: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
    };
  }

  interface PaletteOptions {
    secondary50?: PaletteOptions["primary"];
    success50?: PaletteOptions["primary"];
    info50?: PaletteOptions["primary"];
    error50: PaletteOptions["primary"];
    neutral?: {
      main?: string;
      100?: string;
      200?: string;
      300?: string;
      400?: string;
      500?: string;
      600?: string;
    };
  }

  interface TypographyVariants {
    pLarge: React.CSSProperties;
    pMedium: React.CSSProperties;
    pSmall: React.CSSProperties;
    pSmallest: React.CSSProperties;
  }

  // Allow usage of pLarge in variant prop
  interface TypographyVariantsOptions {
    pLarge?: React.CSSProperties;
    pMedium?: React.CSSProperties;
    pSmall?: React.CSSProperties;
    pSmallest?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    pLarge: true;
    pMedium: true;
    pSmall: true;
    pSmallest: true;
  }
}

// Accent colors that stay constant across modes — status colors read fine on
// either a light or a dark surface. `primary` is NOT here: across this
// codebase it's used throughout as the "ink" color for titles/icons/text
// (via `color="primary"` or `theme.palette.primary.main`), so it has to flip
// with the page background like `text.primary` does. The few spots that use
// primary.main as a solid button background paired with hardcoded white text
// read `primary.contrastText` instead, which MUI computes per mode.
const brandPalette = {
  secondary: { main: "#F2DA8E" },
  secondary50: { main: "#F2DA8E" },
  success: { main: "#368548" },
  success50: { main: "#368548" },
  info: { main: "#6579FC" },
  info50: { main: "#6579FC4D" },
  error: { main: "#FF6B79" },
  error50: { main: "#FF6B79" },
  warning: { main: "#F0CD55" },
};

const modePalettes: Record<
  PaletteMode,
  {
    primary: { main: string };
    background: { default: string; paper: string };
    text: { primary: string; secondary: string };
    neutral: {
      main: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
    };
    // MUI's own default `grey` scale (theme.palette.grey[50..900]) is used
    // throughout this codebase as ad hoc "ink"/"surface" colors, exactly
    // like `neutral` above — but MUI does NOT flip it per mode on its own,
    // it's the same fixed hex ramp regardless of `mode`. Overriding it here
    // (inverted for dark) fixes every one of those call sites at once.
    grey: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
    divider: string;
  }
> = {
  light: {
    primary: { main: "#161616" },
    background: { default: "#F3F4F8", paper: "#ffffff" },
    text: { primary: "#1a1a1a", secondary: "#555" },
    neutral: {
      main: "#FFFFFF",
      "100": "#EFEFF2",
      "200": "#EAEBF0",
      "300": "#D6D8E4",
      "400": "#CCCFDF",
      "500": "#B5BAD3",
      "600": "#7179A3",
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
    },
    divider: "#EAEBF0",
  },
  dark: {
    primary: { main: "#EDEDED" },
    background: { default: "#121212", paper: "#1E1E1E" },
    text: { primary: "#F2F2F2", secondary: "#A9AAB0" },
    neutral: {
      main: "#1E1E1E",
      "100": "#2A2A2E",
      "200": "#333338",
      "300": "#45454C",
      "400": "#57575F",
      "500": "#75757F",
      "600": "#9C9CA8",
    },
    grey: {
      50: "#1a1a1a",
      100: "#242424",
      200: "#333333",
      300: "#454545",
      400: "#5c5c5c",
      500: "#8a8a8a",
      600: "#a8a8a8",
      700: "#c2c2c2",
      800: "#dcdcdc",
      900: "#f2f2f2",
    },
    divider: "#333338",
  },
};

export const getTheme = (mode: PaletteMode): Theme =>
  createTheme({
    palette: {
      mode,
      ...modePalettes[mode],
      ...brandPalette,
    },
    spacing: 7,
    typography: {
      fontFamily: "'Inter', 'Manrope', sans-serif",
      // Base size MUI uses to scale every variant it computes itself
      // (h5, h6, subtitle1/2, caption, overline, the "button" variant...).
      // Knocking it down ~10% from the 14px default shrinks all of those in
      // one place; the variants below are hand-set in px/rem and need their
      // own ~10% trim since they bypass that scaling.
      fontSize: 12.6,
      h1: {
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "31px",
        fontWeight: "bold",
      },
      h2: {
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "25px",
        fontWeight: "bold",
      },
      h3: {
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "19px",
        fontWeight: "bold",
      },
      h4: {
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: "16px",
        fontWeight: "bold",
      },

      body1: { fontFamily: "'Inter'", fontSize: "0.81rem", lineHeight: 1.6 },
      body2: { fontFamily: "'Inter'", fontSize: "0.73rem", lineHeight: 1.5 },

      pLarge: { fontFamily: "'Inter'", fontSize: "14px", fontWeight: "400" },
      pMedium: { fontFamily: "'Inter'", fontSize: "13px", fontWeight: "400" },
      pSmall: { fontFamily: "'Inter'", fontSize: "12px", fontWeight: "400" },
      pSmallest: {
        fontFamily: "'Inter'",
        fontSize: "10px",
        fontWeight: "400",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          body: {
            backgroundColor: theme.palette.background.default,
          },
          // Black-only platform icons turn white in dark mode, wherever they are drawn
          ...(theme.palette.mode === "dark" && {
            [MONOCHROME_PLATFORM_ICONS.map((path) => `img[src$="${path}"]`).join(
              ", "
            )]: { filter: "brightness(0) invert(1)" },
          }),
          "*": {
            scrollbarColor: `${theme.palette.neutral[400]} ${theme.palette.neutral[100]}`,
          },
          "*::-webkit-scrollbar": {
            width: "10px",
            height: "10px",
          },
          "*::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.neutral[100],
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.neutral[400],
            borderRadius: "8px",
            border: `2px solid ${theme.palette.neutral[100]}`,
          },
          "*::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.neutral[500],
          },
        }),
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            boxShadow: "none !important",
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: "50px",
          },
          input: (theme) => ({
            ...theme.theme.typography.pMedium,
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "50px",
            textTransform: "none",
            fontSize: "14px",
            fontFamily: "'Inter'",
            fontWeight: "600",
            padding: "5px 14px",
            minWidth: "58px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "50px",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: "50px",
          },
        },
      },
    },
  });

// Default export kept for any code that still imports the static theme
// directly; prefer `useThemeMode()` / `getTheme(mode)` for mode-aware usage.
const theme = getTheme("light");

export default theme;
